/* Procuração no modelo do escritório: tabela de campos para o outorgante,
   poderes previdenciários e assinatura eletrônica simples.

   A tabela existe para o captador completar à mão, na hora da colheita, o que
   faltou na ficha. Célula vazia é célula para preencher com caneta.

   O documento tenta caber em uma página só. Quando o texto não cabe, o
   parágrafo de poderes é apertado: primeiro o entrelinhas, depois o corpo, e
   nunca abaixo de 8 pt. Se nem assim couber, vai para a segunda página de
   propósito. Procuração ilegível é pior que procuração de duas páginas: o
   cliente é idoso e o documento circula em papel. */

import { jsPDF } from "jspdf";
import { BENEF } from "../dados/catalogo.js";
import { limpaNome, carregarImg } from "../util.js";
import { MARCA } from "../marca.js";
import { ADVOGADO } from "../escritorio.js";


const MESES=["janeiro","fevereiro","março","abril","maio","junho","julho","agosto","setembro","outubro","novembro","dezembro"];

/* Medidas da página, em milímetros. */
const ML=20, MR=20, LARG=210-ML-MR, MEIO=105;
const TOPO=18;      // onde o texto começa, em qualquer página
const LIMITE=268;   // onde o texto tem que parar, antes do rodapé

/* ---------- formatos ---------- */
export const soDig=t=>String(t||"").replace(/\D/g,"");
export function fmtCPF(t){ const d=soDig(t); return d.length===11?`${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`:(t||""); }
export function fmtTel(t){ const d=soDig(t);
  if(d.length===11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
  if(d.length===10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  return t||""; }
/* Código do documento. O caso que já tem código gravado mantém o prefixo dele
   para sempre, inclusive se alguém gerar o PDF de novo: o papel que o cliente
   assinou traz o código antigo, e ele precisa continuar conferindo. Caso novo
   nasce com o prefixo da marca nova. */
export function codigoProc(c){
  const base=(c.id+"|"+(c.cpf||"")+"|"+(c.cliente||"")).toUpperCase();
  let h=0; for(let i=0;i<base.length;i++){ h=(h*31+base.charCodeAt(i))>>>0; }
  const gravado=String(c.procuracao_cod||"");
  const prefixo=gravado.includes("-")?gravado.split("-")[0]:"AXI";
  return prefixo+"-"+h.toString(36).toUpperCase().padStart(7,"0").slice(0,7);
}

/* Largura do texto como ele vai sair desenhado.

   O getTextWidth do jsPDF aplica kerning, mas o doc.text desenha sem kerning
   nenhum. Em palavra com par kernado ("Macapá/AP," por exemplo) a conta sai
   meio milímetro curta, a palavra seguinte começa cedo demais e o espaço
   entre as duas some. Somando letra a letra dá a largura que sai no papel.
   Medido contra o PDF gerado: a diferença cai para menos de um décimo. */
function larguraReal(doc, txt){
  let w=0;
  for(const ch of String(txt||"")) w+=doc.getTextWidth(ch);
  return w;
}

/* ---------- parágrafo com trechos em destaque ----------

   O jsPDF não sabe misturar negrito e itálico dentro de um parágrafo
   justificado: ou o texto inteiro tem um estilo, ou vira remendo. Então a
   montagem é feita aqui, palavra por palavra. Cada palavra guarda o próprio
   estilo, e a sobra da linha é distribuída nos vãos para justificar.

   Trecho que começa colado no anterior (a vírgula logo depois de um termo em
   itálico, por exemplo) vira um pedaço da mesma palavra, senão a quebra de
   linha jogaria essa vírgula sozinha para a linha de baixo. */

function quebrarParagrafo(doc, partes, larg, corpo){
  doc.setFontSize(corpo);
  const palavras=[];
  partes.forEach((p,i)=>{
    const txt=String(p.t||""); if(!txt.trim()) return;
    const estilo=p.e||"normal";
    const ant=partes[i-1];
    const colado = i>0 && ant && !/\s$/.test(String(ant.t||"")) && !/^\s/.test(txt) && palavras.length>0;
    txt.trim().split(/\s+/).forEach((t,j)=>{
      if(j===0 && colado) palavras[palavras.length-1].frags.push({t,estilo});
      else palavras.push({frags:[{t,estilo}]});
    });
  });
  palavras.forEach(p=>{
    p.w=p.frags.reduce((s,f)=>{ doc.setFont("helvetica",f.estilo); return s+larguraReal(doc,f.t); },0);
  });

  doc.setFont("helvetica","normal");
  const espaco=larguraReal(doc," ");
  const linhas=[]; let atual=[], usado=0;
  palavras.forEach(p=>{
    if(atual.length && usado+espaco+p.w>larg){ linhas.push({palavras:atual,usado}); atual=[]; usado=0; }
    usado+=(atual.length?espaco:0)+p.w;
    atual.push(p);
  });
  if(atual.length) linhas.push({palavras:atual,usado});
  return {linhas,espaco};
}

function escreverParagrafo(doc, quebra, x, y, larg, corpo, entre, justificar){
  doc.setFontSize(corpo);
  quebra.linhas.forEach((ln,i)=>{
    if(y>LIMITE){ doc.addPage(); y=TOPO; }
    const vaos=ln.palavras.length-1;
    const vao=(justificar && i<quebra.linhas.length-1 && vaos>0 && ln.usado<larg)
      ? quebra.espaco+(larg-ln.usado)/vaos
      : quebra.espaco;
    let cx=x;
    ln.palavras.forEach((p,j)=>{
      p.frags.forEach(f=>{
        doc.setFont("helvetica",f.estilo);
        doc.text(f.t,cx,y);
        cx+=larguraReal(doc,f.t);
      });
      if(j<vaos) cx+=vao;
    });
    y+=entre;
  });
  return y;
}

/* Corpos e entrelinhas em ordem de preferência: aperta o espaço entre as
   linhas antes de reduzir a letra, e para de reduzir a letra em 8 pt. */
const CORPOS=[9.5,9,8.5,8];
const FATORES=[1.34,1.26,1.18,1.12,1.06];

function ajustarPoderes(doc, partes, larg, disponivel){
  let ultimo=null;
  for(const corpo of CORPOS){
    const quebra=quebrarParagrafo(doc,partes,larg,corpo);
    for(const fator of FATORES){
      const entre=corpo*0.3528*fator;
      ultimo={quebra,corpo,entre};
      if(quebra.linhas.length*entre<=disponivel) return ultimo;
    }
  }
  return ultimo;   // não coube nem no piso de 8 pt: segue e vira duas páginas
}

/* ---------- tabela do outorgante ----------

   Cada linha é uma lista de [rótulo, valor, peso da largura]. O rótulo fica
   com a largura do próprio texto, o valor fica com o resto, e a altura sai do
   valor que mais quebrar: nome comprido e endereço comprido crescem a célula
   em vez de vazar para fora da borda. */

const PAD=1.8, ROT_CORPO=7, VAL_CORPO=9.5, LN_VAL=3.6, ALT_MIN=7.4;

function desenharTabela(doc, linhas, x, y, larg){
  doc.setLineWidth(.2); doc.setDrawColor(120);

  linhas.forEach(segmentos=>{
    const cels=[]; let cx=x;
    segmentos.forEach(([rot,val,peso])=>{
      const w=larg*peso;
      doc.setFont("helvetica","bold"); doc.setFontSize(ROT_CORPO);
      const wRot=Math.min(larguraReal(doc,rot)+PAD*2, w*.62);
      doc.setFont("helvetica","normal"); doc.setFontSize(VAL_CORPO);
      const wVal=w-wRot;
      const texto=doc.splitTextToSize(String(val||""), wVal-PAD*2-0.8);
      cels.push({rot,wRot,wVal,texto,x:cx});
      cx+=w;
    });

    const nMax=cels.reduce((m,cl)=>Math.max(m,cl.texto.length),1);
    const alt=Math.max(ALT_MIN, nMax*LN_VAL+PAD*2.6);

    cels.forEach(cl=>{
      doc.setFillColor(243,244,247);
      doc.rect(cl.x,y,cl.wRot,alt,"FD");
      doc.rect(cl.x+cl.wRot,y,cl.wVal,alt);

      doc.setFont("helvetica","bold"); doc.setFontSize(ROT_CORPO); doc.setTextColor(70);
      doc.text(cl.rot, cl.x+PAD, y+alt/2+1.1);

      doc.setFont("helvetica","normal"); doc.setFontSize(VAL_CORPO); doc.setTextColor(0);
      let ty=y+alt/2-((cl.texto.length-1)*LN_VAL)/2+1.3;
      cl.texto.forEach(t=>{ doc.text(t, cl.x+cl.wRot+PAD, ty); ty+=LN_VAL; });
    });

    y+=alt;
  });

  doc.setDrawColor(0);
  return y;
}

/* ---------- blocos fixos ---------- */

/* Altura reservada para a faixa do timbre, com ou sem logo. Fixa de propósito:
   assim a geometria da página não muda porque uma imagem demorou a carregar, e
   o orçamento do parágrafo elástico continua valendo. */
const LOGO_ALT = 8;

/* Papel timbrado: logo à esquerda, nome e OAB à direita, na mesma faixa.
   Centralizada acima do nome, medido, a logo custa 13 mm do orçamento e joga a
   procuração a rogo para a segunda página. Ao lado custa três. */
async function cabecalho(doc, y){
  const dir = 210 - MR;

  doc.setFont("helvetica","bold"); doc.setFontSize(12.5);
  doc.text(ADVOGADO.nome, dir, y + 4.2, {align:"right"});
  doc.setFont("helvetica","normal"); doc.setFontSize(9); doc.setTextColor(80);
  doc.text(ADVOGADO.oab, dir, y + 8.4, {align:"right"});
  doc.setTextColor(0);

  try{
    const img = await carregarImg(MARCA.arquivo);
    /* Fundo branco explícito e JPEG: a arte tem transparência e degradê metálico,
       e em PNG ela entraria no PDF com quase meio mega. O papel é branco. */
    const cv = document.createElement("canvas");
    cv.width = img.naturalWidth; cv.height = img.naturalHeight;
    const g = cv.getContext("2d");
    g.fillStyle = "#FFFFFF"; g.fillRect(0, 0, cv.width, cv.height);
    g.drawImage(img, 0, 0);
    const larg = Math.min(LOGO_ALT * (cv.width / cv.height), 62);
    doc.addImage(cv.toDataURL("image/jpeg", .92), "JPEG", ML, y, larg, LOGO_ALT, undefined, "FAST");
  }catch{ /* sem arquivo, ou navegador sem WebP: fica só o texto à direita */ }

  const base = y + LOGO_ALT + 2;
  doc.setDrawColor(150); doc.setLineWidth(.3);
  doc.line(ML, base, dir, base); doc.setDrawColor(0);
  return base + 10;
}

function titulo(doc, y){
  doc.setFont("helvetica","bold"); doc.setFontSize(14);
  const t="PROCURAÇÃO", w=larguraReal(doc,t);
  doc.text(t, MEIO, y, {align:"center"});
  doc.setLineWidth(.45);
  doc.line(MEIO-w/2, y+1.3, MEIO+w/2, y+1.3);
  return y+9;
}

function rotulo(doc, txt, y){
  doc.setFont("helvetica","bold"); doc.setFontSize(9.5);
  doc.text(txt, ML, y);
  return y+4.4;
}

/* Altura que o fecho do documento vai ocupar, para o parágrafo de poderes
   saber com quanto espaço pode contar. */
function alturaFecho(assinatura){
  const respiro=7;   // o espaço entre a última linha dos poderes e a data
  const local=11;
  const assina=19+4.6+4.4+4.2+5;
  const rogo=(assinatura && assinatura.modo==="rogo")
    ? 4+4.2*((assinatura.testemunhas||[]).length+1) : 0;
  const caixa=(assinatura?19:21)+5;
  return respiro+local+assina+rogo+caixa;
}

function rodape(doc, cod, assinatura, hj, pag){
  doc.setFont("helvetica","normal"); doc.setFontSize(8); doc.setTextColor(90);
  doc.text(`${ADVOGADO.nome} · ${ADVOGADO.oab}`, MEIO, 275, {align:"center"});
  doc.text(ADVOGADO.endereco+(ADVOGADO.whatsapp?` · WhatsApp ${ADVOGADO.whatsapp}`:""), MEIO, 279, {align:"center"});
  doc.setDrawColor(190); doc.setLineWidth(.2);
  doc.line(ML,282,210-MR,282);
  doc.setFontSize(7.5); doc.setTextColor(120);
  doc.text(
    `Documento ${cod}${assinatura?" · assinatura "+assinatura.codigo:""}`+
    ` · gerado em ${hj.toLocaleDateString("pt-BR")} às ${hj.toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})}`+
    `${pag?" · "+pag:""} · ${MARCA.nome}`,
    MEIO, 286, {align:"center"});
  doc.setTextColor(0); doc.setDrawColor(0);
}

/* ---------- o documento ---------- */

export async function gerarProcuracao(c, assinatura){
  const doc=new jsPDF({unit:"mm",format:"a4"});
  const ben=BENEF[c.beneficio];
  const cod=codigoProc(c);
  const hj=new Date();

  let y=TOPO;
  y=await cabecalho(doc,y);
  y=titulo(doc,y);

  /* --- outorgante, em tabela para completar à mão o que faltar --- */
  y=rotulo(doc,"OUTORGANTE",y);
  const rg=c.rg?(c.rg+(c.rg_orgao?" "+c.rg_orgao:"")):"";
  const tab=[
    [["NOME",(c.cliente||"").toUpperCase(),1]],
    [["NACIONALIDADE",c.nacionalidade||"brasileiro(a)",.40],["ESTADO CIVIL",c.estado_civil||"",.30],["PROFISSÃO",c.profissao||"",.30]],
    [["ENDEREÇO",c.endereco||"",1]],
    [["BAIRRO",c.bairro||"",.34],["CIDADE",c.cidade||"",.30],["UF",(c.uf||"").toUpperCase(),.13],["CEP",c.cep||"",.23]],
    [["RG",rg,.32],["CPF",fmtCPF(c.cpf),.30],["TELEFONE",fmtTel(c.tel1),.38]]
  ];
  /* A linha do e-mail só existe quando o campo existe: muito cliente de zona
     rural não tem, e linha vazia no papel só faz o outorgante perguntar. */
  if(c.email) tab.push([["E-MAIL",c.email,1]]);
  y=desenharTabela(doc,tab,ML,y,LARG)+7;

  /* --- outorgado --- */
  y=rotulo(doc,"OUTORGADO",y);
  const outorgado=[
    {t:ADVOGADO.nome,e:"bold"},
    {t:`, brasileiro, advogado, inscrito na ${ADVOGADO.oab}, com escritório localizado na ${ADVOGADO.endereco}, onde recebe intimações e notificações.`}
  ];
  y=escreverParagrafo(doc,quebrarParagrafo(doc,outorgado,LARG,9.5),ML,y,LARG,9.5,4.4,true)+6;

  /* --- poderes --- */
  y=rotulo(doc,"PODERES",y);
  const poderes=[
    {t:"Amplos poderes para o foro em geral, com a cláusula "},
    {t:"ad judicia et extra",e:"italic"},
    {t:", em qualquer juízo, instância ou tribunal, especialmente para propor Ação Previdenciária em face do Instituto Nacional do Seguro Social (INSS), objetivando o recebimento do benefício a que entender de direito"},
    ...(ben?[{t:`, em especial ${ben.nome.toUpperCase()}${ben.cod?` (espécie ${ben.cod})`:""}`}]:[]),
    {t:", e/ou pela ação que entender oportuna e necessária e contra quem vier a entender, defendê-lo nas contrárias, seguindo umas e outras, usando os recursos legais, acompanhando-a até a final decisão, podendo, ainda, solicitar qualquer documento de propriedade do outorgante, como CNIS, REVSIT, INFBEN, laudos da SABI e HISCRE, como também receber, dar e receber quitação, transigir, transacionar, assumir compromissos, desistir, substabelecer, fazer acordos, prestar declarações, inclusive de pobreza, arrolar, inquirir e reperguntar testemunhas, recorrer de despachos, sentenças e acórdãos, e acompanhar os recursos em geral nas instâncias superiores. Inclusive para levantar valores mediante alvarás judiciais, Requisitórios de Pequeno Valor (RPV) e Precatórios perante a Caixa Econômica Federal, o Banco do Brasil ou qualquer outra instituição bancária, bem como requerer a isenção do imposto de renda para saque do RPV na referida instituição. Ressalta-se que, no que tange ao ingresso de recursos, esta é uma faculdade do(s) advogado(s) contratado(s), ou seja, este(s) somente ingressarão com recursos se entenderem pertinentes, porém, caso o(s) outorgante(s) impulsionem tais recursos, estes poderão ajuizá-los perante a justiça competente. Por fim, praticar todos os demais atos necessários ao cumprimento deste mandato, bem como substabelecê-lo no todo ou em parte."}
  ];
  const ajuste=ajustarPoderes(doc,poderes,LARG,LIMITE-y-alturaFecho(assinatura));
  y=escreverParagrafo(doc,ajuste.quebra,ML,y,LARG,ajuste.corpo,ajuste.entre,true);

  /* --- local e data --- */
  /* Só cabe o fecho inteiro, ou ele vai inteiro para a página seguinte: data
     em uma página e assinatura em outra é documento que ninguém aceita. */
  if(y+alturaFecho(assinatura)>LIMITE){ doc.addPage(); y=TOPO; }
  else y+=7;
  doc.setFont("helvetica","normal"); doc.setFontSize(10);
  const cidade=[c.cidade,(c.uf||"").toUpperCase()].filter(Boolean).join("/");
  doc.text(`${cidade||"____________"}, ${hj.getDate()} de ${MESES[hj.getMonth()]} de ${hj.getFullYear()}.`,ML,y);
  y+=11;

  /* --- assinatura --- */
  const LINHA=100, X1=MEIO-LINHA/2;
  y+=19;
  if(assinatura && assinatura.imagem){
    /* Encaixa o traço na faixa acima da linha sem distorcer. O canvas da tela
       é bem mais alto que a faixa, e esticar deixaria a assinatura achatada. */
    try{
      const p=doc.getImageProperties(assinatura.imagem);
      const escala=Math.min((LINHA-10)/p.width, 17/p.height);
      const w=p.width*escala, h=p.height*escala;
      doc.addImage(assinatura.imagem,"PNG",MEIO-w/2,y-h-1,w,h,undefined,"FAST");
    }catch{ /* imagem inválida: segue com a linha em branco */ }
  }
  doc.setLineWidth(.3); doc.line(X1,y,X1+LINHA,y); y+=4.6;

  doc.setFont("helvetica","bold"); doc.setFontSize(8.5);
  doc.text("OUTORGANTE",MEIO,y,{align:"center"}); y+=4.4;
  doc.setFont("helvetica","normal"); doc.setFontSize(9);
  doc.text((c.cliente||"").toUpperCase(),MEIO,y,{align:"center"}); y+=4.2;
  doc.setFontSize(8.5); doc.setTextColor(80);
  doc.text("CPF "+fmtCPF(c.cpf),MEIO,y,{align:"center"}); y+=5;
  doc.setTextColor(0);

  if(assinatura && assinatura.modo==="rogo"){
    doc.setFontSize(8.5);
    doc.text(`Assinado a rogo por ${(assinatura.rogo||"").toUpperCase()}, a pedido do(a) outorgante.`,MEIO,y,{align:"center"}); y+=4.2;
    (assinatura.testemunhas||[]).forEach((t,i)=>{
      doc.text(`Testemunha ${i+1}: ${t}`,MEIO,y,{align:"center"}); y+=4.2;
    });
    y+=4;
  }

  /* --- carimbo da assinatura, ou instrução de como assinar --- */
  doc.setDrawColor(190); doc.setLineWidth(.2);
  if(assinatura){
    const q=new Date(assinatura.quando);
    doc.roundedRect(ML,y,LARG,19,2,2);
    doc.setFont("helvetica","bold"); doc.setFontSize(8);
    doc.text("ASSINADO ELETRONICAMENTE",ML+5,y+6);
    doc.setFont("helvetica","normal"); doc.setFontSize(8);
    doc.text(doc.splitTextToSize(
      `Assinatura eletrônica simples, nos termos da Lei 14.063/2020. `+
      `Colhida em ${q.toLocaleDateString("pt-BR")} às ${q.toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})}, `+
      `por ${assinatura.colheu||"não informado"}. Código de verificação ${assinatura.codigo}.`, LARG-10),ML+5,y+10.5);
  }else{
    doc.roundedRect(ML,y,LARG,21,2,2);
    doc.setFont("helvetica","bold"); doc.setFontSize(8);
    doc.text("ASSINATURA",ML+5,y+6);
    doc.setFont("helvetica","normal"); doc.setFontSize(8);
    doc.text(doc.splitTextToSize(
      "Assine à mão sobre a linha, ou no portal oficial do Gov.br em assinador.iti.br, com a conta gov.br do(a) outorgante. "+
      "Não sabendo ou não podendo assinar, admite-se assinatura a rogo com duas testemunhas identificadas.",LARG-10),ML+5,y+10.5);
  }
  doc.setDrawColor(0);

  /* --- rodapé em todas as páginas --- */
  const total=doc.internal.getNumberOfPages();
  for(let p=1;p<=total;p++){
    doc.setPage(p);
    rodape(doc,cod,assinatura,hj,total>1?`página ${p} de ${total}`:"");
  }

  const arquivo=`Procuracao${assinatura?"-assinada":""}-${limpaNome(c.cliente||"cliente")}.pdf`;
  doc.save(arquivo);
  return { cod, arquivo, blob: doc.output("blob"), paginas: total, corpo: ajuste.corpo };
}
